<script setup lang="ts">
import { computed, onMounted } from "vue";
import { RouterView, useRoute } from "vue-router";

// api
import { userApi } from "@/api/user";

// components
import { GLoader } from "@gwrk/g-components";
import GNavbar from "@/components/molecules/Navbar.vue";
import GSidebarMenu from "@/components/molecules/SidebarMenu.vue";

// composables
import { useI18n } from "vue-i18n";
import { useOpenState } from "@gwrk/g-components/composables";

// interfaces
import type { LinkWithSubLinks } from "@gwrk/g-components/interfaces";

// enums
import { Size } from "@gwrk/g-components/enums";
import { LegacyClientPanelUrl } from "@/enums/LegacyClientPanelUrl";
import { Route } from "@/enums/Route";
import { UserFeature } from "@/enums/UserFeature";

// stores
import { useCompanyId } from "@/stores/useCompanyId";
import { useUser } from "@/stores/useUser";
import { useUiFeatures } from "@/stores/useUiFeatures";

const { t } = useI18n();
const companyIdStore = useCompanyId();
const userStore = useUser();
const uiFeaturesStore = useUiFeatures();
const route = useRoute();
const {
  isOpen: isSidebarMenuOpen,
  toggle: toggleSidebarMenu,
  close: closeSidebarMenu,
} = useOpenState();

const checkCompanyId = () => {
  const companyId = window.location.pathname.split("/")?.[2] ?? "";

  if (!companyId) {
    console.error("Company ID is not provided, redirecting to login page...");
    window.location.href = LegacyClientPanelUrl.PANEL;
  }

  companyIdStore.setCompanyId({
    companyId,
  });
};

const updateCompanyLastActivity = async () => {
  try {
    await userApi().updateLastActivity({
      companyId: companyIdStore.companyId,
    });
  } catch (err: any) {
    console.error(err);
  }
};

checkCompanyId();

const sidebarLinks = computed<LinkWithSubLinks[]>(() => [
  {
    title: t("navigation.job_offers"),
    href: {
      name: Route.JOB_OFFERS_LISTING_ACTIVE,
      params: {
        companyId: companyIdStore.companyId,
      },
    },
    selected: route.name === Route.JOB_OFFERS_LISTING_ACTIVE,
    disabled: false,
    subLinks: [],
  },
  {
    title: t("navigation.job_offer_templates"),
    href: {
      name: Route.JOB_OFFER_TEMPLATES_LISTING,
      params: {
        companyId: companyIdStore.companyId,
      },
    },
    selected:
      route.name === Route.JOB_OFFER_TEMPLATES_LISTING ||
      route.name === Route.JOB_OFFER_ADD_TEMPLATE ||
      route.name === Route.JOB_OFFER_EDIT_TEMPLATE,
    disabled: false,
    subLinks: [],
  },
  {
    title: t("navigation.reviews"),
    href: `/panel/${companyIdStore.companyId}/opinie`,
    selected: false,
    disabled: !userStore.user.privileges.is_review_moderator,
    subLinks: [],
  },
  {
    title: t("navigation.qaa"),
    href: `/panel/${companyIdStore.companyId}/pytania-i-odpowiedzi`,
    selected: false,
    disabled: !userStore.userFeatures[UserFeature.QAA_SHOW],
    subLinks: [],
  },
  {
    title: t("navigation.news"),
    href: `/panel/${companyIdStore.companyId}/aktualnosci`,
    selected: false,
    disabled: !userStore.user.privileges.is_news_author,
    subLinks: [],
  },
  {
    title: t("navigation.notifications"),
    href: `/panel/${companyIdStore.companyId}/ustawienia/powiadomienia`,
    selected: false,
    disabled: false,
    subLinks: [],
  },
  {
    title: t("navigation.customer_care"),
    href: `/panel/${companyIdStore.companyId}/customer-care`,
    selected: false,
    disabled: false,
    subLinks: [],
  },
  {
    title: t("navigation.eb_news"),
    href: `/panel/${companyIdStore.companyId}/eb`,
    selected: false,
    disabled: false,
    subLinks: [],
  },
]);

onMounted(() => {
  window.addEventListener("resize", closeSidebarMenu);

  updateCompanyLastActivity();
});
</script>

<template>
  <div class="app">
    <GLoader
      v-if="userStore.isLoading || uiFeaturesStore.isLoading"
      :is-absolute-position="true"
      :size="Size.LG"
      class="!z-20"
    />

    <GNavbar @toggle="toggleSidebarMenu" />

    <main class="app__main">
      <GSidebarMenu
        class="app__sidebar-menu"
        :links="sidebarLinks"
        :class="{ 'app__sidebar-menu--open': isSidebarMenuOpen }"
        @close-sidebar="closeSidebarMenu"
      />

      <div class="app__router-view-container">
        <div class="scroll-placeholder" />

        <div class="container py-4 mt-16 mb-8 lg:my-0">
          <RouterView />
        </div>
      </div>
    </main>
  </div>
</template>

<style scoped lang="scss">
.app {
  display: flex;
  flex-direction: column;
  height: 100vh;

  &__main {
    display: grid;
    overflow: hidden;
    position: relative;

    @screen md {
      grid-template-columns: 16rem auto;
    }
  }

  &__sidebar-menu {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    transform: translateX(-100%);
    z-index: 10;
    transition: 0.3s ease-in-out;
    height: 100%;

    &--open {
      opacity: 1;
      transform: translateX(0);
    }

    @screen md {
      position: initial;
      opacity: 1;
      transform: translateX(0);
    }
  }

  &__router-view-container {
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: smooth;
    min-height: calc(100vh - 5rem); // height of navbar
  }
}
</style>
